import {
  BlogPost,
  CompanyInfo,
  EarningsCalendarItem,
  EarningsInDetail,
  Move,
} from '@oai-frontend-monorepo/types';
import createClient from '../create-client';

type QuoteItem = {
  symbol: string;
  name: string;
  price: number;
  marketCap: number;
  changesPercentage: number;
  change: number;
  volume: number;
  avgVolume: number;
  previousClose: number;
  timestamp: number;
  pe: number;
};

export interface ExtendedEarningsCalendarItem extends EarningsCalendarItem {
  quote: QuoteItem;
}

export class ToolsApiClient {
  private client: ReturnType<typeof createClient>;

  constructor(getToken?: () => Promise<string | undefined>) {
    this.client = createClient(``, getToken);
  }

  public registerGetToken(
    baseUrl: string,
    getToken?: () => Promise<string | undefined>
  ) {
    this.client = createClient(baseUrl, getToken);

    return this;
  }

  public async getHasOptions(symbol: string) {
    const res = await this.client.get<boolean>(
      `/has-options?symbol=${symbol}`,
      undefined,
      true
    );

    return res;
  }

  public async getBlogPosts(page: number, pageSize: number) {
    if (page < 0 || pageSize <= 0) {
      throw new Error('Invalid page or pageSize');
    }

    const res = await this.client.get<{
      page: number;
      pageSize: number;
      data: BlogPost[];
    }>(`/blog?page=${page}&pageSize=${pageSize}`, undefined, true);

    return res;
  }

  public async getBlogPost(id: string) {
    if (!id) {
      throw new Error('ID is required');
    }
    const res = await this.client.get<BlogPost>(`/blog/${id}`, undefined, true);
    if (!res) {
      throw new Error('Blog post not found');
    }
    return res;
  }

  public async getCompany(symbol?: string) {
    if (!symbol) {
      throw new Error('Symbol is required');
    }

    const res = await this.client.get<CompanyInfo[]>(
      `/companies/${symbol}`,
      undefined,
      true
    );

    return res?.[0];
  }

  public async getEarnings(symbol?: string) {
    if (!symbol) {
      throw new Error('Symbol is required');
    }

    const res = await this.client.get<EarningsInDetail>(
      `/earnings?symbol=${symbol}`,
      undefined,
      true
    );

    return res;
  }

  public async getNearestMove(
    symbol: string,
    date: string,
    reportTime?: string | null
  ) {
    if (!symbol || !date) {
      throw new Error('Symbol and date are required');
    }

    const res = await this.client.get<Move>(
      `/moves/nearest?symbol=${symbol}&expirationDate=${date}${
        reportTime ? `&reportTime=${reportTime}` : ''
      }`,
      undefined,
      true
    );

    return res;
  }

  public getEarningsCalendar(from: string, to: string) {
    if (!from || !to) {
      throw new Error('From and to dates are required');
    }

    return this.client.get<ExtendedEarningsCalendarItem[]>(
      `/earnings-calendar?from=${from}&to=${to}`,
      undefined,
      true
    );
  }

  public async postUserQuestion(name: string, question: string) {
    if (!name || !question) {
      throw new Error('Name and question are required');
    }

    const res = await this.client.post<string>(`/user-questions`, {
      name,
      question,
    });

    return res;
  }
}

export default new ToolsApiClient();
